import { Component, EventEmitter, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { UpdatedColumnDetails } from "src/app/core/helpers/helper";

@Component({
  selector: "app-add-column-modal",
  templateUrl: "./add-column-modal.component.html",
  styleUrl: "./add-column-modal.component.scss",
  standalone: false
})
export class AddColumnModalComponent {
  addedCoumns: Array<any> = [];
  checkedColumns: Array<any> = [];
  extraColumns: Array<any> = [];

  @Output() columnAddEvent: EventEmitter<UpdatedColumnDetails> = new EventEmitter();
  @ViewChild("modal", { static: true }) modal!: TemplateRef<any>;

  constructor(public offcanvasService: NgbOffcanvas) {}

  /**
   * Opens the modal dialog with the provided columns.
   * Initializes the checked columns based on the extra columns.
   *
   * @param addedCoumns The columns that have already been added.
   * @param extraColumns The additional columns that can be added.
   */
  openModal(addedCoumns: any, extraColumns: Array<any>): void {
    this.extraColumns = extraColumns;
    this.addedCoumns = addedCoumns;
    this.checkedColumns = [];
    this.extraColumns.forEach((ec: any) => {
      if (!ec.isHidden) {
        this.checkedColumns.push(ec);
      }
    });
    this.offcanvasService.open(this.modal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" })?.result.then(
      () => {},
      () => {}
    );
  }

  /**
   * Handles the change event of a checkbox.
   * Adds or removes the column from the checked columns array based on the checkbox state.
   *
   * @param event The checkbox change event.
   * @param index The index of the column in the extra columns array.
   */
  onCheckboxChange(event: any, index: number): void {
    const isChecked = event.target.checked;
    if (isChecked) {
      this.checkedColumns.push(this.extraColumns[index]);
    } else {
      const columnIndex = this.checkedColumns.findIndex((x) => x.key === this.extraColumns[index]?.key);
      if (columnIndex !== -1) {
        this.checkedColumns.splice(columnIndex, 1);
      }
    }
  }

  /**
   * Checks if the add button should be shown for a given column.
   *
   * @param item The column to check.
   * @returns True if the add button should be shown, false otherwise.
   */
  isShowAddBtn(item: any): boolean {
    return !this.addedCoumns.some((ac: any) => ac.key === item.key);
  }

  /**
   * Adds the selected columns to the checked columns array and emits the columnAddEvent.
   *
   * @param item The column to add. If null, adds all checked columns.
   */
  onColumnAdd(item: any = null): void {
    if (item) {
      this.checkedColumns = []; // Clear the array before adding a new selection
      const column: any = this.extraColumns.find((x) => x.key === item.key);
      column.isHidden = false;
      this.checkedColumns.push(column);
    } else {
      this.checkedColumns.forEach((col: any) => {
        col.isHidden = false;
      });
    }

    this.updateChekedColumnsArray();

    this.columnAddEvent.emit({ columns: this.checkedColumns, isAdd: true });
    this.closeModal();
  }

  /**
   * Removes the selected columns from the checked columns array and emits the columnAddEvent.
   *
   * @param item The column to remove. If null, removes all checked columns.
   */
  onColumnRemove(item: any = null): void {
    if (item) {
      const column: any = this.checkedColumns.find((x: any) => x.key === item.key);
      column.isHidden = true;
    } else {
      this.checkedColumns.forEach((col: any) => {
        col.isHidden = true;
      });

      this.updateChekedColumnsArray();
    }

    this.columnAddEvent.emit({ columns: this.checkedColumns, isAdd: false });
    this.closeModal();
  }

  /**
   * Updates the checked columns array to include all extra columns.
   */
  updateChekedColumnsArray(): void {
    this.extraColumns.forEach((item) => {
      const existsInSecondArray = this.checkedColumns.some((secondItem) => secondItem.key === item.key);
      if (!existsInSecondArray) {
        this.checkedColumns.push(item);
      }
    });
  }

  /**
   * Handles the change event of the "select all" checkbox.
   * Selects or deselects all extra columns based on the checkbox state.
   *
   * @param event The checkbox change event.
   */
  onAllCheckboxChange(event: any): void {
    if (event.target.checked) {
      this.checkedColumns = JSON.parse(JSON.stringify(this.extraColumns));
    } else {
      this.checkedColumns = [];
    }
  }

  /**
   * Checks if a column is in the checked columns array.
   *
   * @param key The key of the column to check.
   * @returns True if the column is checked, false otherwise.
   */
  isChecked(key: string): boolean {
    return this.checkedColumns.some((x) => x.key === key);
  }

  /**
   * Checks if all extra columns are in the checked columns array.
   *
   * @returns True if all columns are checked, false otherwise.
   */
  isAllChecked(): boolean {
    return this.checkedColumns.length === this.extraColumns.length;
  }

  /**
   * Closes the currently open modal dialog.
   */
  closeModal(): void {
    this.offcanvasService.dismiss(this.modal);
  }
}
